import React from 'react';
import PropTypes from 'prop-types';

import ImageCropperModal from '../image_cropper/ImageCropperModal';
import ImageError, { imageErrorTypes } from './ImageError';
import Instructions from '../PhotoInstructions';
import Uploader from './Uploader';

import SegmentAnalytics from 'helpers/SegmentAnalytics.js';

const loadImage = require('blueimp-load-image');

class ImageUploader extends React.Component {
  acceptedFiletypes = '.jpeg, .jpg, .bmp, .gif, .png';
  minImageSize = { width: 300, height: 300 };

  instructionsShown = false;

  constructor(props) {
    super(props);

    this.state = {
      imageFile: undefined,
      imageDataURL: undefined,
      instructionsOpen: false,
      imageCropperOpen: false,
      imageErrorModalOpen: false,
      imageErrorType: undefined
    };
  }

  render() {
    return (
      <span className={this.props.className}>
        { this.props.children }

        {this.state.instructionsOpen && (
          <Instructions
            showDermoscopicInstructions={this.props.showDermoscopicInstructions}
            onInstructionsClosed={this.onInstructionsClosed.bind(this)}
          />
        )}

        {this.state.imageCropperOpen &&
          <ImageCropperModal
            imageData={this.state.imageDataURL}
            onBoundingBoxChange={this.onBoundingBoxChange.bind(this)}
            onImageCropperClosed={this.onImageCropperClosed.bind(this)}
            minBoundingSize={Math.min(...Object.values(this.minImageSize))}
          />
        }

        {this.state.imageErrorModalOpen &&
          <ImageError
            errorType={this.state.imageErrorType}
            onClosed={this.onImageErrorModalClosed.bind(this)}
          />
        }

        <span
          style={{ 'display': 'none' }}
          ref={(c) => { this.uploader = c; }}>
          <Uploader
            filetypes={this.acceptedFiletypes}
            onUploading={this.props.onUploading}
            onUpload={this.onUpload.bind(this)}
            onError={this.props.onError}
            onChange={this.onFilesSelected.bind(this)}
          />
        </span>
      </span>
    );
  }

  onBoundingBoxChange(boundingBox) {
    this.boundingBox = boundingBox;
  }

  onImageCropperClosed(wasImageCropped) {
    this.setState({ imageCropperOpen: false });

    if (wasImageCropped) {
      this.triggerUpload(this.state.imageFile);
    }
  }

  onInstructionsClosed() {
    this.props.onPhotoInstructionsShown(true); // We don't really need to pass anything
    this.instructionsShown = true;

    this.setState({ instructionsOpen: false });

    this.triggerNativeUploader();
  }

  onFilesSelected(files, callback) {
    if (!files || !files.length) { return; }

    SegmentAnalytics.track('Image file selected');

    const file = files[0];

    this.triggerUpload = callback;

    if (this.props.markAsUploadingOnSelection) {
      this.props.onUploading();
    }

    if (!window.FileReader) {
      this.triggerUpload(file);
      return;
    }

    const reader = new FileReader();

    reader.onload = ((readerEvent) => {
      const blob = readerEvent.target.result;

      loadImage.parseMetaData(file, (data) => {
        const options = { canvas: true };

        if (data.exif && data.exif.get('Orientation')) {
          options.orientation = data.exif.get('Orientation');
        }

        loadImage(blob, (canvas) => {
          const imageError = this.checkImageValidity(file, canvas);

          if (imageError) {
            this.showError(imageError);
            return;
          }

          try {
            const dataURL = canvas.toDataURL(file.type);

            this.setState({
              imageFile: file,
              imageDataURL: dataURL
            });

            if (this.props.showImageCropper) {
              this.setState({
                imageCropperOpen: true
              });
            } else {
              this.triggerUpload(file);
            }
          } catch {
            this.showError(imageErrorTypes.unknown);
          }
        }, options);
      });
    });

    reader.readAsDataURL(file);
  }

  onImageErrorModalClosed() {
    this.setState({
      imageErrorModalOpen: false
    }, this.startUpload);
  }

  onUpload(image) {
    SegmentAnalytics.track('Image uploaded');

    this.props.onUpload({
      ...image,
      imageDataURL: this.state.imageDataURL,
      crop: this.boundingBox
    });
  }

  checkImageValidity(file, canvas) {
    if (!this.acceptedFiletypes.includes(file.name.split('.').pop().toLowerCase())) { return imageErrorTypes.extension; }
    if (canvas.type === 'error') { return imageErrorTypes.unknown; }
    if (!this.areImageDimensionsValid(canvas)) { return imageErrorTypes.dimensions; }
  }

  areImageDimensionsValid(canvas) {
    const { width, height } = canvas;

    return width >= this.minImageSize.width
        && height >= this.minImageSize.height;
  }

  showError(errorType) {
    this.props.onError();

    this.setState({
      imageErrorModalOpen: true,
      imageErrorType: errorType
    });
  }

  startUpload() {
    if (this.props.showInstructions && !this.instructionsShown) {
      this.setState({ instructionsOpen: true });
    } else {
      this.triggerNativeUploader();
    }
  }

  triggerNativeUploader() {
    this.uploader.querySelector('input[type="file"]').click();
  }
}

ImageUploader.defaultProps = {
  className: '',
  showImageCropper: true,
  showInstructions: true,
  showDermoscopicInstructions: false,
  markAsUploadingOnSelection: false,
  onPhotoInstructionsShown: () => {},
  onUploading: () => {},
  onError: () => {}
};

ImageUploader.propTypes = {
  className: PropTypes.string,
  children: PropTypes.any.isRequired,
  onError: PropTypes.func,
  onPhotoInstructionsShown: PropTypes.func,
  onUpload: PropTypes.func.isRequired,
  onUploading: PropTypes.func,
  showImageCropper: PropTypes.bool,
  showInstructions: PropTypes.bool,
  showDermoscopicInstructions: PropTypes.bool,
  markAsUploadingOnSelection: PropTypes.bool
};

export default ImageUploader;
